import { useNavigate, useSearchParams } from 'react-router-dom';

import { useUserBalanceStore } from '@/entities/balance';
import { useCurrentUser } from '@/entities/user/currentUser';

import { API } from '@/shared/api';
import { PATHS } from '@/shared/constants/routes/paths';
import {
  PAYMENT_CREDITS_CANCELED_PARAM,
  PAYMENT_SUBSCRIPTION_CANCELED_PARAM,
  PAYMENT_SUCCESSFUL_PARAM,
} from '@/shared/constants/routes/searchParams';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { getPlanPeriodLabel } from '@/shared/lib/subscription';
import { GTMEvents } from '@/shared/thirdParty/gtm';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { getLatestPaymentData } from './utils/getLatestPaymentData';

// Убирает из урла "payment-successful" и отправляет событие в аналитику
export const useHandlePaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: user } = useCurrentUser();
  const { hasActiveSubscription, planName } = useUserBalanceStore();

  const periodLabel = getPlanPeriodLabel(user?.subscription?.plan.interval);

  useExecuteOneTime(async () => {
    const hasPaymentSuccessfulParam = searchParams.has(PAYMENT_SUCCESSFUL_PARAM);
    const hasPaymentSubscriptionCancelParam = searchParams.has(PAYMENT_SUBSCRIPTION_CANCELED_PARAM);
    const hasPaymentCreditsCancelParam = searchParams.has(PAYMENT_CREDITS_CANCELED_PARAM);

    if (hasPaymentSubscriptionCancelParam) {
      MixpanelEvents.paymentSubscriptionCanceled();
      searchParams.delete(PAYMENT_SUBSCRIPTION_CANCELED_PARAM);
      setSearchParams(searchParams);
    }

    if (hasPaymentCreditsCancelParam) {
      MixpanelEvents.paymentCreditsCanceled();
      searchParams.delete(PAYMENT_CREDITS_CANCELED_PARAM);
      setSearchParams(searchParams);
    }

    if (hasPaymentSuccessfulParam) {
      if (user?.credits) {
        MixpanelEvents.paymentSuccessful({
          minutes: user.credits.minutes.total,
        });
      }

      searchParams.delete(PAYMENT_SUCCESSFUL_PARAM);
      setSearchParams(searchParams);

      const paramsLength = Array.from(searchParams.keys()).length;
      // Оставляем в урле остальные параметры
      // TODO: type needs to be corrected
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      navigate(PATHS.homepage + (paramsLength ? `?${searchParams}` : ''), { replace: true });

      const latestPaymentInfo = await API.getLatestPaymentInfo();

      if (latestPaymentInfo) {
        const { name, latestPaymentPeriod, quantity, range } = getLatestPaymentData(latestPaymentInfo);

        GTMEvents.purchase(latestPaymentInfo);

        if (latestPaymentPeriod === 'Annual' || latestPaymentPeriod === 'Monthly') {
          const eventData = {
            hasActiveSubscription,
            subscription: `${name} ${latestPaymentPeriod}`,
          };

          MixpanelEvents.paymentSuccessfulSubscription(eventData);
          return;
        }

        const eventData = {
          hasActiveSubscription,
          minutes: quantity,
          minutes_range: range,
          subscription: `${planName} ${periodLabel}`,
        };

        MixpanelEvents.paymentSuccessfulMinutes(eventData);
      }
    }
  });
};
